import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { cn } from '@/lib/utils';

import type { NotificationsReport } from '@/types/report.types';

interface NotificationsChartProps {
  data: NotificationsReport;
}

export function NotificationsChart({ data }: NotificationsChartProps) {
  const { t } = useTranslation();
  const { overall } = data;

  const templateData = data.byTemplate.map((tpl) => ({
    name: tpl.templateName.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    deliveryRate: tpl.deliveryRatePercent,
    totalSent: tpl.totalSent,
  }));

  return (
    <div className='rounded-lg bg-white p-6 shadow'>
      <h3 className='text-lg font-semibold text-gray-900 mb-4'>{t('reports.notifications.title')}</h3>

      {/* Stat cards */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3 mb-6'>
        <div className='rounded-lg border border-gray-200 p-4'>
          <p className='text-xs font-medium text-gray-500'>{t('reports.notifications.totalSent')}</p>
          <p className='text-2xl font-bold text-gray-900 mt-1'>{overall.totalSent}</p>
        </div>
        <div className='rounded-lg border border-gray-200 p-4'>
          <p className='text-xs font-medium text-gray-500'>{t('reports.notifications.deliveryRate')}</p>
          <p
            className={cn(
              'text-2xl font-bold mt-1',
              overall.deliveryRatePercent >= 95 ? 'text-green-600' : 'text-gray-900',
            )}>
            {overall.deliveryRatePercent}%
          </p>
        </div>
        <div className='rounded-lg border border-gray-200 p-4'>
          <p className='text-xs font-medium text-gray-500'>{t('reports.notifications.bounced')}</p>
          <p className={cn('text-2xl font-bold mt-1', overall.bouncedCount > 0 ? 'text-amber-600' : 'text-gray-900')}>
            {overall.bouncedCount}
          </p>
        </div>
        <div className='rounded-lg border border-gray-200 p-4'>
          <p className='text-xs font-medium text-gray-500'>{t('reports.notifications.failed')}</p>
          <p className={cn('text-2xl font-bold mt-1', overall.failedCount > 0 ? 'text-red-600' : 'text-gray-900')}>
            {overall.failedCount}
          </p>
        </div>
      </div>

      {/* Template delivery rates chart */}
      {templateData.length > 0 && (
        <div>
          <h4 className='text-sm font-medium text-gray-700 mb-2'>{t('reports.notifications.byTemplate')}</h4>
          <ResponsiveContainer width='100%' height={250}>
            <BarChart data={templateData} layout='vertical' margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray='3 3' horizontal={false} />
              <XAxis type='number' domain={[0, 100]} tick={{ fontSize: 12 }} />
              <YAxis type='category' dataKey='name' width={140} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value) => [`${value}%`, t('reports.notifications.deliveryRate')]} />
              <Bar dataKey='deliveryRate' fill='#22c55e' radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Channel breakdown */}
      {data.byChannel.length > 0 && (
        <div className='mt-4'>
          <h4 className='text-sm font-medium text-gray-700 mb-2'>{t('reports.notifications.byChannel')}</h4>
          <div className='overflow-x-auto'>
            <table className='min-w-full text-xs'>
              <thead>
                <tr className='border-b border-gray-200'>
                  <th className='py-2 pr-4 text-left font-medium text-gray-500'>Channel</th>
                  <th className='py-2 px-2 text-right font-medium text-gray-500'>
                    {t('reports.notifications.totalSent')}
                  </th>
                  <th className='py-2 px-2 text-right font-medium text-gray-500'>
                    {t('reports.notifications.delivered')}
                  </th>
                  <th className='py-2 px-2 text-right font-medium text-gray-500'>
                    {t('reports.notifications.bounced')}
                  </th>
                  <th className='py-2 px-2 text-right font-medium text-gray-500'>
                    {t('reports.notifications.deliveryRate')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.byChannel.map((ch) => (
                  <tr key={ch.channel} className='border-b border-gray-100'>
                    <td className='py-2 pr-4 font-medium text-gray-700'>{ch.channel.toUpperCase()}</td>
                    <td className='py-2 px-2 text-right text-gray-600'>{ch.metrics.totalSent}</td>
                    <td className='py-2 px-2 text-right text-gray-600'>{ch.metrics.deliveredCount}</td>
                    <td className='py-2 px-2 text-right text-gray-600'>{ch.metrics.bouncedCount}</td>
                    <td className='py-2 px-2 text-right text-gray-600'>{ch.metrics.deliveryRatePercent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
